import {
  ApplicationForChecklist,
  Checklist,
  ChecklistStatus,
  SailorChecklistItem,
} from "../types";
import { DocumentData } from "firebase/firestore";
import { calculateSailorCheckList } from "./calculateSailorCheckList";
import {
  actionBlocked,
  affordabilityCheckDone,
  boatDetailsValid,
  calculateCreditApproved,
  mcaDetailsValid,
  progressTrackerActionIsComplete,
  progressTrackerCheckIsComplete,
} from "../util";

const checkBothChecklists = (
  application: ApplicationForChecklist,
  mainSailorChecklist: Record<SailorChecklistItem, ChecklistStatus>,
  jointSailorChecklist: Record<SailorChecklistItem, ChecklistStatus>,
  check: SailorChecklistItem
): ChecklistStatus => {
  const mainApplicantStatus = mainSailorChecklist[check];
  const jointApplicantStatus: ChecklistStatus = application.isJoint
    ? jointSailorChecklist[check]
    : "not-applicable";
  if (mainApplicantStatus === false || jointApplicantStatus === false) {
    return false;
  }
  if (mainApplicantStatus !== true) {
    return mainApplicantStatus;
  }
  if (
    (application.isJoint ? jointApplicantStatus : mainApplicantStatus) === true
  ) {
    return true;
  }
  return null;
};
export const calculateApplicationCheckList = (
  application: ApplicationForChecklist,
  applicationProgressTracker: Record<string, unknown> | undefined,
  applicantProgressTracker: Record<string, unknown> | undefined,
  jointApplicantProgressTracker: Record<string, unknown> | undefined,
  applicantRiskProfile: DocumentData | undefined,
  jointApplicantRiskProfile: DocumentData | undefined,
  affordabilityReport: DocumentData | undefined
) => {
  const mainSailorCheckList = calculateSailorCheckList(
    application.applicant?.sailor,
    applicantProgressTracker,
    applicantRiskProfile
  );
  const jointSailorCheckList = calculateSailorCheckList(
    application.jointApplicant?.sailor,
    jointApplicantProgressTracker,
    jointApplicantRiskProfile
  );
  const checkList: Checklist = {
    quoteValid: !!application.quoteValid,
    personalDetails: mainSailorCheckList.personalDetails,
    personalDetailsJointApplicant: jointSailorCheckList.personalDetails,
    incomeDetails: mainSailorCheckList.incomeDetails,
    incomeDetailsJointApplicant: jointSailorCheckList.incomeDetails,
    supportingDocumentation: mainSailorCheckList.supportingDocumentation,
    supportingDocumentationJointApplicant:
      jointSailorCheckList.supportingDocumentation,
    kycAmlConsent: checkBothChecklists(
      application,
      mainSailorCheckList,
      jointSailorCheckList,
      "kycAmlConsent"
    ),
    openBankingConsent: checkBothChecklists(
      application,
      mainSailorCheckList,
      jointSailorCheckList,
      "openBankingConsent"
    ),
    boatDealerAssigned:
      !!application.dealer &&
      progressTrackerCheckIsComplete(applicationProgressTracker, "dealerInfo"),

    idVerified: checkBothChecklists(
      application,
      mainSailorCheckList,
      jointSailorCheckList,
      "idVerified"
    ),
    kycAmlCheckDone: checkBothChecklists(
      application,
      mainSailorCheckList,
      jointSailorCheckList,
      "kycAmlCheckDone"
    ),
    //todo: add checklist? sailor.openBankingConsent===true (no does not mean fail - manual process involved)
    affordabilityCheckDone: affordabilityCheckDone(affordabilityReport),
    creditApproved: calculateCreditApproved(applicationProgressTracker),
    surveySupplied: progressTrackerActionIsComplete(
      applicationProgressTracker,
      "survey"
    ),
    invoiceSupplied: progressTrackerActionIsComplete(
      applicationProgressTracker,
      "invoices"
    ),
    insuranceSupplied: progressTrackerActionIsComplete(
      applicationProgressTracker,
      "insurance"
    ),
    boatDocumentationSupplied: progressTrackerActionIsComplete(
      applicationProgressTracker,
      "boatDocumentation"
    ),
    boatSpecificationUpdated: boatDetailsValid(application.vessel),
    //todo: loanDocumentsGenerated does not exist we could store a bool value
    loanDocumentsGenerated: "not-applicable",
    completionDateSet: !!application.completionDate || null,
    mcaRegistration: mcaDetailsValid(application),
    documentsSigned: progressTrackerActionIsComplete(
      applicationProgressTracker,
      "loanDocs"
    ),
  };

  const creditCheckBlock = actionBlocked(checkList, [
    "personalDetails",
    "personalDetailsJointApplicant",
    "incomeDetails",
    "incomeDetailsJointApplicant",
    "kycAmlConsent",
    "quoteValid",
  ]);

  const docGenBlock =
    creditCheckBlock ||
    actionBlocked(checkList, [
      "supportingDocumentation",
      "supportingDocumentationJointApplicant",
      "openBankingConsent",
      "kycAmlCheckDone",
      "boatDealerAssigned",
      "idVerified",
      "affordabilityCheckDone",
      "creditApproved",
      "surveySupplied",
      "invoiceSupplied",
      "insuranceSupplied",
      "boatDocumentationSupplied",
      "boatSpecificationUpdated",
    ]);
  const blocks = {
    creditCheck: creditCheckBlock,
    documentGeneration: docGenBlock,
    convertToAgreement:
      docGenBlock ||
      actionBlocked(checkList, [
        "loanDocumentsGenerated",
        "completionDateSet",
        "mcaRegistration",
        "documentsSigned",
      ]),
  };
  return { ...checkList, blocks };
};
